// DataSourceEntryRowActions — the entries table's kebab, carrying the same three
// actions the selection bar offers: Edit · Export · Delete. Here they always act
// on this one row, so there is no single/multi split — that only applies to the
// bar.
//
// All three callbacks are OPTIONAL: the page passes `undefined` when the verb
// (edit / the export extra / delete) is missing and the item doesn't render —
// absence means hidden. There is no read item, so with none of the three the
// kebab renders nothing at all.

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface DataSourceEntryRowActionsProps {
  /** Absent = hidden. */
  onEdit?: () => void;
  /** Absent = hidden. */
  onExport?: () => void;
  /** Absent = hidden. */
  onDelete?: () => void;
}

const DataSourceEntryRowActions = ({
  onEdit,
  onExport,
  onDelete,
}: DataSourceEntryRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const stop = (e: MouseEvent) => e.stopPropagation();
  const pick = (run: () => void) => (e: MouseEvent) => {
    stop(e);
    setOpen(false);
    run();
  };

  if (!onEdit && !onExport && !onDelete) return null;

  return (
    <span onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        portal
        width={200}
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('datasource_entries_row_actions')}
            onClick={(e) => {
              stop(e);
              setOpen((o) => !o);
            }}
          />
        }
      >
        {onEdit && (
          <button
            type='button'
            role='menuitem'
            className='app-dropdown__item'
            onClick={pick(onEdit)}
          >
            <Icon name='square-pen' size={16} />
            <span className='app-dropdown__item-label'>{t('datasource_entries_action_edit')}</span>
          </button>
        )}
        {onExport && (
          <button
            type='button'
            role='menuitem'
            className='app-dropdown__item'
            onClick={pick(onExport)}
          >
            <Icon name='download' size={16} />
            <span className='app-dropdown__item-label'>{t('datasource_action_export')}</span>
          </button>
        )}
        {onDelete && (
          <button
            type='button'
            role='menuitem'
            className='app-dropdown__item app-dropdown__item--danger'
            onClick={pick(onDelete)}
          >
            <Icon name='trash-2' size={16} />
            <span className='app-dropdown__item-label'>{t('button_delete')}</span>
          </button>
        )}
      </Dropdown>
    </span>
  );
};

export default DataSourceEntryRowActions;
